/**
 * Eat in or take away, asked first.
 *
 * It comes before the menu because it *changes* the menu: `onlyTakeIn` products
 * exist, and a product the customer cannot have should not be on the screen
 * they are choosing from.
 *
 * Two targets and nothing else. They are stacked rather than side by side
 * because the panel is portrait and a full-width card is a target a customer
 * cannot miss with a thumb — which is the whole job of this screen.
 *
 * **The cards fill the reach band and the question sits above it.** On a
 * floor-standing panel the top of the glass is above the eyeline and out of
 * reach from a wheelchair, so the heading is up there where it is read and the
 * two cards start below the halfway line where they are pressed. The back way
 * out is in the action bar with every other screen's, rather than in the
 * corner it used to hide in.
 */
import { useT } from '@/i18n'
import { useKiosk } from '@/store/kiosk'
import { Wordmark } from '@/components/brand'
import { ActionBar, BackButton } from '@/components/ui'

export function OrderType() {
  const t = useT()
  const choose = useKiosk((s) => s.chooseOrderType)
  const setStep = useKiosk((s) => s.setStep)

  const choice = (kind: 'eat_in' | 'take_out', label: string, hint: string, glyph: string) => (
    <button
      type="button"
      onClick={() => choose(kind)}
      /*
        The whole card fills with the brand on press. On a resistive panel with
        no hover there is no other moment to confirm the touch landed, and a
        border that thickens by two pixels is not that moment.
      */
      className="tap group relative flex flex-1 flex-col items-center justify-center gap-7 overflow-hidden rounded-[var(--radius-tile)] bg-[var(--color-paper-sunk)] p-12 shadow-[var(--shadow-lift)] transition-colors duration-300 active:bg-[var(--color-brand)]"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-12 -right-8 text-[18rem] leading-none opacity-[0.07] transition-opacity duration-300 group-active:opacity-25"
      >
        {glyph}
      </span>
      <span aria-hidden="true" className="text-[9rem] leading-none drop-shadow-lg">
        {glyph}
      </span>
      <span className="display text-[length:var(--text-kiosk-2xl)] uppercase">{label}</span>
      <span className="display-upright text-[length:var(--text-kiosk-lg)] text-[var(--color-ink-faint)] group-active:text-[var(--color-brand-ink)]/70">
        {hint}
      </span>
    </button>
  )

  return (
    <div className="flex h-full flex-col bg-[var(--color-paper-raised)]">
      {/*
        The question, in the band that is for reading. It has the mark over it
        because this is the first screen after the attract loop and a customer
        who tapped a yellow panel should still be able to see whose it was.
      */}
      <header className="flex shrink-0 flex-col items-center gap-8 px-10 pt-14 pb-10">
        <Wordmark className="w-64" />
        <h1 className="display text-center text-[length:var(--text-kiosk-xl)] uppercase">{t('orderType.title')}</h1>
      </header>

      <div className="mx-auto flex w-full min-h-0 flex-1 flex-col gap-8 px-10 pb-8">
        {choice('eat_in', t('orderType.eatIn'), t('orderType.eatInHint'), '☕️')}
        {choice('take_out', t('orderType.takeOut'), t('orderType.takeOutHint'), '🥤')}
      </div>

      <ActionBar>
        <BackButton label={t('common.back')} onClick={() => setStep('welcome')} />
        {/*
          Not filler. The cart carries a per-line order type and the checkout
          screen has the control for it, but nothing told the customer that
          before they committed here — so a person buying a coffee to drink and
          a cake to take read this screen as a choice they had to get right.
        */}
        <p className="text-[length:var(--text-kiosk-sm)] text-white/45">{t('orderType.perLineHint')}</p>
      </ActionBar>
    </div>
  )
}
